import Axios from "../config/axios-config";
import { errorHandler } from "../utils/errorHandler";
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../config/constants';

export const Login = async (values) => {
  try {
    const login = await Axios.post("/auth/sign-in", values);

    if (!login.data.message) {
      try {
        const { accessToken, refreshToken } = login.data;
        localStorage.setItem(`${ACCESS_TOKEN}`, accessToken);
        localStorage.setItem(`${REFRESH_TOKEN}`, refreshToken);
      } catch (ex) {
        console.error("Error al guardar tokens:", ex);
      }
    }

    return { success: true, data: login.data, error: null };
  } catch (error) {
    // Detectar bloqueo por no pago (403 con code PAYMENT_BLOCKED)
    const isPaymentBlocked = error.response?.data?.code === 'PAYMENT_BLOCKED';
    if (isPaymentBlocked) {
      return {
        success: false,
        data: null,
        error: {
          message: error.response.data.message,
          code: 'PAYMENT_BLOCKED',
          statusCode: 403,
        },
      };
    }

    // Detectar si es un error de CORS antes de aplicar el mensaje personalizado
    const isCorsError = errorHandler.isCorsError(error);

    // Solo usar mensaje personalizado si NO es un error de CORS
    // El errorHandler ya manejará el mensaje de CORS apropiadamente
    const customMessage = isCorsError
      ? null
      : (error.response?.data?.message || "Error al iniciar sesión. Verifica tus credenciales.");

    // Sin aviso acá: lo muestran login.jsx y acceptInvite.jsx con su propio texto, y
    // notificar en los dos lados duplicaba el aviso de "usuario o contraseña incorrectos".
    const errorInfo = errorHandler.handleApiError(error, {
      showNotification: false,
      customMessage: customMessage,
      context: "Login"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

// Inicia sesión con el `id_token` que devuelve Google Identity Services.
//
// El endpoint se llama `/auth/google-mobile` por historia (nació para la app de
// repartidores), pero es agnóstico del cliente: valida el token contra Google y emite los
// mismos tokens que /auth/sign-in. El header `x-app: web` ya viaja en todas las llamadas
// (ver config/axios-config.js) y es lo que hace que el backend aplique el carril web.
export const LoginWithGoogle = async (idToken) => {
  try {
    const response = await Axios.post("/auth/google-mobile", { idToken });

    try {
      const { accessToken, refreshToken } = response.data;
      localStorage.setItem(`${ACCESS_TOKEN}`, accessToken);
      localStorage.setItem(`${REFRESH_TOKEN}`, refreshToken);
    } catch (ex) {
      console.error("Error al guardar tokens:", ex);
    }

    return { success: true, data: response.data, error: null };
  } catch (error) {
    const status = error.response?.status;
    const payload = error.response?.data;

    // 409: el correo de Google no tiene cuenta. En la web no se auto-registra a nadie (los
    // usuarios del portal se crean por invitación), así que se informa y punto.
    if (status === 409 && payload?.needsRegistration) {
      return {
        success: false,
        data: null,
        error: { code: "NEEDS_REGISTRATION", statusCode: 409 },
      };
    }

    // Códigos que la vista traduce a un mensaje propio: cuenta suspendida por deuda y
    // rechazos por rol (repartidor entrando a la web, superadmin del sistema).
    const knownCode = payload?.code;
    if (knownCode === "PAYMENT_BLOCKED" || knownCode === "WEB_ACCESS_NOT_ALLOWED" || knownCode === "GOOGLE_LOGIN_NOT_ALLOWED") {
      return {
        success: false,
        data: null,
        error: { code: knownCode, message: payload?.message, statusCode: status },
      };
    }

    const isCorsError = errorHandler.isCorsError(error);
    const customMessage = isCorsError ? null : (payload?.message || "No se pudo iniciar sesión con Google.");

    const errorInfo = errorHandler.handleApiError(error, {
      showNotification: false,
      customMessage,
      context: "Login con Google",
    });

    return { success: false, data: null, error: errorInfo };
  }
};

// Solicita la recuperación de acceso. `values` = { email, method } donde method es
// 'link' (enlace para cambiar contraseña) o 'code' (código de un solo uso).
export const recoveryPassword = async (values) => {
  try {
    const response = await Axios.post("/auth/request-password-reset", values);
    return { success: true, data: response.data, error: null };
  } catch (error) {
    const status = error.response?.status;

    // NO se notifica desde acá. El endpoint responde 200 tanto si el correo está
    // registrado como si no (respuesta uniforme, anti-enumeración), así que la vista
    // muestra siempre el mismo mensaje de éxito. Un toast de error disparado desde esta
    // capa se superponía a esa pantalla y producía un estado contradictorio: éxito y
    // error a la vez. Quién avisa y qué avisa lo decide la vista.
    errorHandler.handleApiError(error, {
      showNotification: false,
      context: "Recuperación de contraseña",
    });

    // Se clasifican únicamente condiciones que no dependen de si la cuenta existe:
    // el 429 habla del ritmo de peticiones de este cliente, y el resto de que la
    // petición no llegó a destino. Ninguna de las dos revela nada sobre el correo.
    return {
      success: false,
      data: null,
      error: { code: status === 429 ? "RATE_LIMITED" : "REQUEST_FAILED", statusCode: status },
    };
  }
};

// The only text the reset-password service uses for an invalid, used or expired
// token. The DTO validation messages never equal it, which is what makes the check
// inside confirmRecovery safe now that the BFF joins validation messages into one
// string.
const INVALID_RECOVERY_TOKEN_MESSAGE = "El enlace de recuperación es inválido o expiró.";

// Flujo de enlace: cambia la contraseña con el token. La respuesta puede traer
// { accessToken, refreshToken } (auto-login) o { autologin:false, code } si el
// usuario no puede ingresar (p. ej. suspensión por pago).
export const confirmRecovery = async (values) => {
  try {
    const response = await Axios.post("/auth/reset-password", values);
    return { success: true, data: response.data, error: null };
  } catch (error) {
    const status = error.response?.status;
    const backendMessage = error.response?.data?.message;

    // Expired or already used link. It gets its own code so the view can offer a new
    // link: the old generic message ("Intenta nuevamente") invited the user to retry
    // the SAME dead link, which can never work. No enumeration risk here — this
    // operates on a token, not on an email.
    //
    // Recognised by the service's exact text, NOT by the payload shape: the BFF
    // exceptionFactory joins DTO messages into a single STRING ('password must be
    // shorter than or equal to 128 characters'), so a validation 400 arrived with the
    // very structure the old Array.isArray check expected for the dead link, and every
    // 400 was reported as INVALID_TOKEN.
    if (status === 400 && backendMessage === INVALID_RECOVERY_TOKEN_MESSAGE) {
      return {
        success: false,
        data: null,
        error: { code: "INVALID_TOKEN", message: backendMessage, statusCode: 400 },
      };
    }

    // Everything else is notified here: validation 400s show the backend text through
    // apiBodyMessage, network failures show the connection text, CORS stays CORS. The
    // 429 gets no fallbackMessage so it keeps the handler's per-status Spanish text
    // instead of a generic one that says nothing about the rate limit.
    const errorInfo = errorHandler.handleApiError(error, {
      showNotification: true,
      context: "Confirmar recuperación",
      ...(status === 429
        ? {}
        : { fallbackMessage: "No se pudo restablecer la contraseña. Intenta nuevamente." }),
    });

    return { success: false, data: null, error: errorInfo };
  }
};

// The blocked-account text the BFF emits as a 400/401 after the OTP is burned.
const BLOCKED_USER_TEXT = "user is blocked";

// Flujo de código (passwordless): valida el OTP e inicia sesión. La respuesta trae
// { accessToken, refreshToken }. No muestra notificación automática: el componente
// decide el mensaje (código inválido, cuenta suspendida, etc.), así que acá solo se
// clasifica la causa y se devuelve un `code` estable junto al texto del handler.
export const recoveryCodeLogin = async (values) => {
  try {
    const response = await Axios.post("/auth/recovery-code-login", values);
    return { success: true, data: response.data, error: null };
  } catch (error) {
    const isPaymentBlocked = error.response?.data?.code === 'PAYMENT_BLOCKED';
    if (isPaymentBlocked) {
      return {
        success: false,
        data: null,
        error: {
          message: error.response.data.message,
          code: 'PAYMENT_BLOCKED',
          statusCode: 403,
        },
      };
    }

    // Logging only (showNotification: false): the view owns the user-facing text, so
    // no customMessage here either.
    const errorInfo = errorHandler.handleApiError(error, {
      showNotification: false,
      context: "Acceso por código"
    });

    const status = error.response?.status ?? null;
    const backendMessage = error.response?.data?.message;

    // The service funnels EVERY OTP failure — unknown user, no OTP, expired,
    // attempts exhausted, wrong code — into the same 400 'Código inválido o
    // expirado.', on purpose (anti-enumeration). The only failure carrying its own
    // text is the blocked account, which surfaces right after the OTP is burned, so
    // it is matched by that text and not by "any 400 that is not the uniform one":
    // a DTO 400 ('email must be an email') is not a blocked account.
    const isBlockedUser =
      (status === 400 || status === 401) &&
      typeof backendMessage === "string" &&
      backendMessage.toLowerCase().includes(BLOCKED_USER_TEXT);

    let code;
    if (!error.response || status >= 500) code = "NETWORK";
    else if (status === 429) code = "RATE_LIMITED";
    else if (isBlockedUser) code = "ACCOUNT_BLOCKED";
    else code = "INVALID_CODE";

    return {
      success: false,
      data: null,
      error: { code, message: errorInfo?.message ?? null, statusCode: status },
    };
  }
};
